import { useCallback, useMemo, useState } from "react";
import {
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  CookieConsentContext,
  type ConsentCategory,
  type ConsentState,
  type StoredConsent,
} from "@/hooks/use-cookie-consent";

// Read any previously stored consent. Returns null if none, malformed, or from
// an older policy version (so we re-prompt the visitor).
function readStoredConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed.version !== CONSENT_VERSION) return null;
    return {
      necessary: true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
    };
  } catch {
    return null;
  }
}

function persistConsent(state: ConsentState): void {
  try {
    const payload: StoredConsent = {
      version: CONSENT_VERSION,
      updatedAt: new Date().toISOString(),
      analytics: state.analytics,
      marketing: state.marketing,
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be unavailable (private mode). Consent then lives only
    // for the current session in memory, which is acceptable.
  }
}

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsent] = useState<ConsentState | null>(() => readStoredConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const apply = useCallback((next: ConsentState) => {
    setConsent(next);
    persistConsent(next);
    setPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    apply({ necessary: true, analytics: true, marketing: true });
  }, [apply]);

  const rejectAll = useCallback(() => {
    apply({ necessary: true, analytics: false, marketing: false });
  }, [apply]);

  const savePreferences = useCallback(
    (categories: { analytics: boolean; marketing: boolean }) => {
      apply({ necessary: true, analytics: categories.analytics, marketing: categories.marketing });
    },
    [apply],
  );

  const hasConsent = useCallback(
    (category: ConsentCategory) => (consent ? consent[category] === true : false),
    [consent],
  );

  const value = useMemo(
    () => ({
      consent,
      isDecided: consent !== null,
      preferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
      acceptAll,
      rejectAll,
      savePreferences,
      hasConsent,
    }),
    [consent, preferencesOpen, acceptAll, rejectAll, savePreferences, hasConsent],
  );

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}
